import React, { useState } from 'react';
import {
  ChevronLeftUniversal,
  MoreInfoOutlined,
  SettingsOutlined,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  ConfigProvider,
  Modal,
  Typography,
} from '@duxton-web/ui';
import styled from 'styled-components';

import '@/features/variable/settings/product-entity/manage-entities/index.css';

import EntitiesList from '@/features/variable/settings/product-entity/manage-entities/entities-list';

const ManageEntitiesTitle = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
          },
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <Button
          data-testid="manage-entities-back"
          shape="circle"
          ghost
          size="small"
          type="secondary"
          onClick={onCancel}
          icon={<ChevronLeftUniversal />}
        />
        <Typography.Title level={3}>Manage Product Names</Typography.Title>
      </div>
    </ConfigProvider>
  );
};

const ManageEntitiesModal = ({
  open = false,
  onCreate = () => {},
  onCancel = () => {},
  onItemSelect = () => {},
}: {
  open?: boolean;
  onCreate?: () => void;
  onCancel?: () => void;
  onItemSelect?: () => void;
}) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [isDisplayNone, setIsDisplayNone] = useState<boolean>(false);

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      className={`manage-entities-modal ${isDisplayNone ? 'transparent' : ''}`}
      mask={false}
      open={open}
      data-testid="manage-entities-modal"
      centered
      title={<ManageEntitiesTitle onCancel={handleCancel} />}
      onCancel={handleCancel}
      styles={{
        body: {
          overflow: 'hidden',
          height: 'calc(100vh - 138px)',
        },
      }}
      width={700}
      footer={null}
      closable={false}
      maskClosable={false}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Alert
          type="info"
          message={
            <div style={{ display: 'flex', gap: 8 }}>
              <MoreInfoOutlined
                style={{
                  alignSelf: 'flex-start',
                  color: '#136FD8',
                  width: '20px',
                  height: '20px',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Typography.Body level={2} strong>
                  Product Name should represent the group of variables required
                  for a rollout
                </Typography.Body>
                <Typography.Text>
                  When assigning a Product Name, choose a granularity which
                  makes sense for users to conduct rollouts together.
                </Typography.Text>
                <Info showmore={showMore ? 'true' : 'false'}>
                  <Typography.Text>
                    For example, a product might be &ldquo;GrabShare&rdquo;, but
                    there are multiple components that make it work, such as:
                    <ul>
                      <li>Waiting time</li>
                      <li>
                        Pricing / Discounts and their effect on passengers and
                        drivers
                      </li>
                      <li>Route matching</li>
                    </ul>
                  </Typography.Text>
                  <Typography.Text>
                    Each of these might have both a frontend and backend
                    variable that enables them to work together. Translating the
                    above, the product names tagged to variables could be:
                    <ul>
                      <li>GrabShare Waiting Time</li>
                      <li>GrabShare Pricing</li>
                      <li>GrabShare Route Matching</li>
                    </ul>
                  </Typography.Text>
                </Info>

                <a
                  onClick={(_) => {
                    setShowMore(!showMore);
                  }}
                >
                  {showMore ? 'Show Less' : 'Show More'} ...
                </a>
              </div>
            </div>
          }
        />
        <EntitiesList
          style={{ flex: '1 1 auto', overflow: 'auto' }}
          onCreateClick={() => {
            setIsDisplayNone(true);
          }}
          onCreateOk={() => {
            setIsDisplayNone(false);

            onCreate();
          }}
          onCreateCancel={() => {
            setIsDisplayNone(false);
          }}
          onDeleteClick={() => {
            setIsDisplayNone(true);
          }}
          onDeleteCancel={() => {
            setIsDisplayNone(false);
          }}
          onDeleteOk={() => {
            setIsDisplayNone(false);
          }}
          onItemSelect={() => {
            setIsDisplayNone(false);

            onItemSelect();
          }}
        />
      </div>
    </Modal>
  );
};

const ManageProductEntities = ({
  onManageEntitiesClick = () => {},
  onManageEntitiesCancel = () => {},
}: {
  onManageEntitiesClick?: () => void;
  onManageEntitiesCancel?: () => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <Button
        data-testid="manage-entities-button"
        shape="circle"
        type="secondary"
        size="small"
        onClick={() => {
          setOpen(true);

          onManageEntitiesClick();
        }}
        icon={<SettingsOutlined />}
      />
      <ManageEntitiesModal
        open={open}
        onCreate={() => {
          setOpen(false);

          onManageEntitiesCancel();
        }}
        onCancel={() => {
          setOpen(false);

          onManageEntitiesCancel();
        }}
        onItemSelect={() => {
          setOpen(false);

          onManageEntitiesCancel();
        }}
      />
    </>
  );
};

const Info = styled.div<{ showmore?: string }>`
  display: flex;
  flex-direction: column;
  height: ${({ showmore }) => (showmore === 'true' ? '236px' : '0px')};
  transition:
    height 0.4s,
    visibility 0.2s;
  visibility: ${({ showmore }) => (showmore === 'true' ? 'visible' : 'hidden')};
`;

export default ManageProductEntities;
